import { useFeatureFlagContext, useOverriddenFeatures, checkIsBotVisit, getDeviceInfo, Platform, FeatureFlagsDocument, FeatureFlagCacheValueFragmentDoc, parseDocumentFeatureFlagCookies, FeatureFlagsContextProvider } from "./styles/components/FeatureFlagsController.mjs";
import { FeatureFlagProvider, FeatureFlagsController, extractFeatureFlagsCookies } from "./styles/components/FeatureFlagsController.mjs";
import { useMemo } from "react";
import { jsx } from "react/jsx-runtime";
const toExperimentKeys = (keys) => isSingleExperiment(keys) ? keys.toLowerCase() : keys.map((k) => k.toLowerCase());
const isSingleExperiment = (key) => !Array.isArray(key);
const useIsExperimentsReady = () => {
  const { isExperimentsReady } = useFeatureFlagContext();
  return isExperimentsReady;
};
const useExperiment = (key) => {
  const { experiments } = useFeatureFlagContext();
  const overriddenExperiments = useOverriddenFeatures({
    features: experiments ?? []
  });
  return useMemo(
    () => overriddenExperiments.find(
      (flag) => toExperimentKeys(flag.key) === toExperimentKeys(key)
    ) || void 0,
    [key, overriddenExperiments]
  );
};
const useExperimentVariation = (key) => useExperiment(key)?.variation;
const useExperimentValue = (key) => useExperiment(key)?.value;
const useFeatureFlag = (key) => {
  const { featureFlags } = useFeatureFlagContext();
  const overriddenFeatureFlags = useOverriddenFeatures({
    features: featureFlags ?? []
  });
  const featureFlagData = useMemo(
    () => overriddenFeatureFlags.find(
      (flag) => toExperimentKeys(flag.key) === toExperimentKeys(key)
    ),
    [overriddenFeatureFlags, key]
  );
  return featureFlagData?.value;
};
const getExperimentsTrackingNumbersMap = (keys, experimentsConfig) => keys.reduce((allExperiments, k) => {
  const key = toExperimentKeys(k);
  const { trackingNumber } = experimentsConfig?.find(
    (experiment) => toExperimentKeys(experiment.key) === key
  ) ?? {};
  if (trackingNumber) {
    allExperiments[key] = trackingNumber;
  }
  return allExperiments;
}, {});
const useOverriddenEnrolledExperiments = () => {
  const { enrolledExperiments, experiments, hasOverrides } = useFeatureFlagContext();
  return useOverriddenFeatures({
    features: (hasOverrides ? experiments : enrolledExperiments) ?? []
  });
};
const useHubbleExperiments = () => {
  const enrolledExperiments = useOverriddenEnrolledExperiments();
  return useMemo(() => {
    if (!enrolledExperiments) {
      return {};
    }
    return enrolledExperiments.reduce(
      (allExperiments, { key, variation }) => {
        allExperiments[key] = {
          name: key,
          participantStatus: "enrolled",
          variant: variation
        };
        return allExperiments;
      },
      {}
    );
  }, [enrolledExperiments]);
};
const useHubbleEligibleProps = () => {
  const { applicationName } = useFeatureFlagContext();
  const hubbleExperiments = useHubbleExperiments();
  const enrolledExperiments = useOverriddenEnrolledExperiments();
  const appPrefix = applicationName ? `${applicationName}:` : "";
  return useMemo(() => {
    if (!enrolledExperiments) {
      return {};
    }
    return {
      experiments: hubbleExperiments,
      solMetadataTags: enrolledExperiments.reduce(
        (allTags, { key, variation }) => {
          if (variation) {
            allTags[`seek:${appPrefix}experiments:${key}`] = variation;
          }
          return allTags;
        },
        {}
      )
    };
  }, [enrolledExperiments, hubbleExperiments, appPrefix]);
};
const useTealiumEligibleProps = () => {
  const { experimentsConfig } = useFeatureFlagContext();
  const hubbleExperiments = useHubbleExperiments();
  const enrolledExperiments = useOverriddenEnrolledExperiments();
  const experimentsKeyTrackingNumberMap = useMemo(
    () => getExperimentsTrackingNumbersMap(
      enrolledExperiments.map((flag) => flag.key) ?? [],
      experimentsConfig
    ),
    [enrolledExperiments, experimentsConfig]
  );
  return useMemo(() => {
    if (!enrolledExperiments) {
      return {};
    }
    return {
      experimentsEligible: enrolledExperiments.reduce(
        (all, { key, variation }) => {
          all.push({
            id: key,
            variant: variation,
            num: experimentsKeyTrackingNumberMap[key]
          });
          return all;
        },
        []
      ),
      hubbleExperiments
    };
  }, [enrolledExperiments, experimentsKeyTrackingNumberMap, hubbleExperiments]);
};
const useExperimentsEligibleProps = () => ({
  tealiumEligibleProps: useTealiumEligibleProps(),
  hubbleEligibleProps: useHubbleEligibleProps()
});
const POTENTIAL_REASONS = {
  SKIP_PREFETCH_FEATURE_FLAGS_DUE_TO_EMPTY_SOL_ID: "Feature flags were not requested because solId was missing.",
  SKIP_PREFETCH_FEATURE_FLAGS_DUE_TO_BOT_VISIT: "Feature flags were not requested because the user is a bot.",
  SKIP_PREFETCH_FEATURE_FLAGS_DUE_TO_NO_FLAGS: "Feature flags were not requested because no flags were provided.",
  FAILED_TO_PREFETCH_FEATURE_FLAGS: "Feature flags were not prefetched because an error occurred while fetching them:"
};
const fetchFeatureFlagsOutsideOfReactLifecycle = async (apolloClient, {
  customContext,
  experimentsConfig,
  featureFlagsConfig,
  solId,
  userAgent,
  brand,
  country: countryProp,
  language,
  locale
}) => {
  const flags = [...featureFlagsConfig, ...experimentsConfig].map(
    (flag) => flag.key
  );
  if (!solId) {
    return {
      success: false,
      code: "SKIP_PREFETCH_FEATURE_FLAGS_DUE_TO_EMPTY_SOL_ID",
      error: new Error(
        POTENTIAL_REASONS.SKIP_PREFETCH_FEATURE_FLAGS_DUE_TO_EMPTY_SOL_ID
      )
    };
  }
  if (checkIsBotVisit(userAgent)) {
    return {
      success: false,
      code: "SKIP_PREFETCH_FEATURE_FLAGS_DUE_TO_BOT_VISIT",
      error: new Error(
        POTENTIAL_REASONS.SKIP_PREFETCH_FEATURE_FLAGS_DUE_TO_BOT_VISIT
      )
    };
  }
  if (flags.length === 0) {
    return {
      success: false,
      code: "SKIP_PREFETCH_FEATURE_FLAGS_DUE_TO_NO_FLAGS",
      error: new Error(
        POTENTIAL_REASONS.SKIP_PREFETCH_FEATURE_FLAGS_DUE_TO_NO_FLAGS
      )
    };
  }
  const country = countryProp.toUpperCase();
  const deviceInfo = getDeviceInfo(userAgent);
  try {
    const { data } = await apolloClient.query({
      query: FeatureFlagsDocument,
      variables: {
        flags,
        visitorContext: { key: solId },
        deviceContext: {
          key: solId,
          locale,
          // TODO: This country should follow the user device country, and not
          // the current site country.
          country,
          ...deviceInfo
        },
        experienceContext: {
          key: solId,
          brand,
          country,
          language
        },
        ...customContext,
        applicationContext: {
          key: solId,
          ...customContext?.applicationContext,
          // Always forcing the platform to be Web for web applications
          channel: Platform.Web
        }
      }
    });
    return {
      success: true,
      data: data?.featureFlags
    };
  } catch (e) {
    if (e instanceof Error) {
      return {
        success: false,
        code: "FAILED_TO_PREFETCH_FEATURE_FLAGS",
        error: e
      };
    }
    return {
      success: false,
      code: "FAILED_TO_PREFETCH_FEATURE_FLAGS",
      error: new Error(
        `${POTENTIAL_REASONS.FAILED_TO_PREFETCH_FEATURE_FLAGS} ${JSON.stringify(e)}`
      )
    };
  }
};
const getFeatureFlagOutsideOfReactLifecycleHelper = ({
  apolloClient,
  overridesGetter = parseDocumentFeatureFlagCookies
}) => (flag, jsonVariations) => {
  const cacheValue = apolloClient.cache.readFragment({
    id: `FeatureFlagEvaluation:${flag}`,
    fragment: FeatureFlagCacheValueFragmentDoc
  });
  const overrideValue = overridesGetter()?.[flag];
  if (typeof overrideValue !== "undefined") {
    if (jsonVariations) {
      return jsonVariations[Number(overrideValue)]?.value;
    }
    return overrideValue;
  }
  if (typeof cacheValue?.value !== "undefined") {
    return cacheValue.value;
  }
};
const FeatureFlagTestProvider = ({
  children,
  mocks
}) => {
  const features = Object.entries(mocks).reduce((allFeatures, current) => {
    const [key, value] = current;
    allFeatures.push({
      id: key,
      key,
      value,
      // Hardcoding reason and variation as they're usually not needed in tests
      reason: "FALLTHROUGH",
      variation: "1"
    });
    return allFeatures;
  }, []);
  return /* @__PURE__ */ jsx(
    FeatureFlagsContextProvider,
    {
      value: {
        isExperimentsReady: true,
        featureFlags: features,
        experiments: features
      },
      children
    }
  );
};
export {
  FeatureFlagProvider,
  FeatureFlagTestProvider,
  FeatureFlagsController,
  extractFeatureFlagsCookies,
  fetchFeatureFlagsOutsideOfReactLifecycle,
  getFeatureFlagOutsideOfReactLifecycleHelper,
  useExperiment,
  useExperimentValue,
  useExperimentVariation,
  useExperimentsEligibleProps,
  useFeatureFlag,
  useIsExperimentsReady
};
